import {
  AutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel,
  TextAreaWithLabel
} from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import {
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography
} from '@material-ui/core'
import { useEffect, useMemo, useState } from 'react'

import { ENTITY_LIST_QUERY } from '../../graphql/entityList'
import { addCommaToPrice, removeCommaFromPrice } from '../../helpers/numbers'
import { persianRegex } from '../../helpers/regex'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { Currency } from '../../types/currency'
import { EntityList } from '../../types/entityList'
import {
  ActionAmountDirection,
  ActionAmountType,
  ActionType,
  actionTypes,
  PriceActionWithEditMode
} from '../../types/priceClass'
import { EntityTypeForRoleManagement, EntityTypes } from '../../types/role'
import { recalcPriceActionErrors } from './priceActionChain'
import useStyles from './styles/AddPriceActionForm.styles'

interface AddPriceActionFormProps {
  setPriceActionWithEditMode: React.Dispatch<
    React.SetStateAction<PriceActionWithEditMode[]>
  >
  index: number
  priceActionWithEditMode: PriceActionWithEditMode[]
  currencies: {
    input: Currency
    output: Currency
  }
  setCurrencies: React.Dispatch<
    React.SetStateAction<{
      input: Currency
      output: Currency
    }>
  >
}

const AddPriceActionForm: React.FC<AddPriceActionFormProps> = ({
  setPriceActionWithEditMode,
  index,
  priceActionWithEditMode,
  currencies,
  setCurrencies
}) => {
  const classes = useStyles()
  const [autoCompleteBeneficiaryValue, setAutoCompleteBeneficiaryValue] =
    useState<any>({
      value: priceActionWithEditMode[index].priceAction?.beneficiary?.entityID,
      label: priceActionWithEditMode[index].priceAction?.beneficiary?.entityName
    })

  const [entityType, setEntityType] = useState<EntityTypeForRoleManagement>()
  const [options, setOptions] = useState<
    {
      label: string
      value: string
      entityCode: string
      displayEntityCode: string
    }[]
  >([])
  const [language, setLanguage] = useState<string>('English')

  const initialValue: PriceActionWithEditMode = useMemo(
    () => priceActionWithEditMode[index],
    []
  )

  const [getEntityList, { loading: loadingForEntityList }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setOptions(data.entityList)
    }
  })

  useEffect(() => {
    if (entityType) {
      getEntityList({
        variables: {
          entityType: entityType
        }
      })
    }
  }, [entityType])

  useEffect(() => {
    if (priceActionWithEditMode[index].priceAction?.beneficiary?.entityType) {
      setEntityType(
        priceActionWithEditMode[index].priceAction.beneficiary
          ?.entityType as EntityTypeForRoleManagement
      )
    } else {
      setEntityType(undefined)
    }
  }, [])

  const addPriceAction = () => {
    if (priceActionWithEditMode && priceActionWithEditMode.length > 0) {
      const current = priceActionWithEditMode[index]
      const outputCurrency = current.priceAction.actionAmount.toCurrency

      setCurrencies({
        ...currencies,
        output: outputCurrency
      })

      const errors = recalcPriceActionErrors(
        priceActionWithEditMode,
        currencies.input,
        outputCurrency
      )

      // then recalc all items' errors based on *new* outputCurrency
      const updatedList = priceActionWithEditMode.map((item, i) => {
        return {
          ...item,
          priceAction: {
            ...item.priceAction,
            order: i + 1 // keep order in sync
          },
          editing: false,
          error: errors[i]
        }
      })

      setPriceActionWithEditMode(updatedList)
    }
  }

  const isPersian = persianRegex.test(
    priceActionWithEditMode[index].priceAction.descriptionTranslatedList[0]
      .value
  )

  const calculateFromCurrency = (
    actionType: ActionType,
    previousToCurrency: Currency,
    currentToCurrency: Currency
  ): Currency => {
    return actionType !== 'Exchange' ? currentToCurrency : previousToCurrency
  }

  // DisplayRate is a sink: it has no beneficiary (like the commission/exchange/discount types here).
  const disabledTypes: ActionType[] = [
    'CommissionPassOn',
    'Exchange',
    'Discount',
    'DisplayRate'
  ]

  const disabled = disabledTypes.includes(
    priceActionWithEditMode[index].priceAction.actionType
  )
  const disabledByExchange =
    priceActionWithEditMode[index].priceAction.actionType === 'Exchange'

  const [localInput, setLocalInput] = useState<string>('')

  return (
    <div className={classes.root}>
      <div className={classes.upSection}>
        <div className={classes.actionsTypeSelector}>
          <SelectWithLabel
            name="Action Type"
            fullWidth
            value={priceActionWithEditMode[index].priceAction.actionType}
            label={getTranslate('Action Type')}
            onChange={(event) => {
              const selectedActionType = event.target.value as ActionType
              setPriceActionWithEditMode([
                ...priceActionWithEditMode.slice(0, index),
                {
                  ...priceActionWithEditMode[index],
                  priceAction: {
                    ...priceActionWithEditMode[index].priceAction,
                    actionType: selectedActionType,
                    actionAmount: {
                      amount: undefined,
                      type: undefined,
                      fromCurrency: '',
                      toCurrency: '',
                      direction:
                        selectedActionType === 'DisplayRate'
                          ? 'Decrease'
                          : undefined
                    },
                    beneficiary: undefined
                  }
                },
                ...priceActionWithEditMode.slice(index + 1)
              ])

              setEntityType(undefined)
              setAutoCompleteBeneficiaryValue(null)
            }}
            size="small">
            {actionTypes.map((ele, ii) => (
              <MenuItem
                key={ii}
                value={ele.value}
                className={classes.menuItemRight}>
                {getTranslate(ele.label)}
              </MenuItem>
            ))}
          </SelectWithLabel>
        </div>
        <div className={classes.amountContainer}>
          <InputWithLabel
            label={getTranslate('Amount')}
            autoComplete="off"
            noHelperText
            fullWidth
            value={
              localInput !== ''
                ? localInput
                : priceActionWithEditMode[index].priceAction.actionType !==
                    'Exchange' &&
                  priceActionWithEditMode[index].priceAction.actionAmount.amount
                ? addCommaToPrice(
                    priceActionWithEditMode[index].priceAction.actionAmount
                      .amount || 0
                  )
                : ''
            }
            onChange={(event) => {
              const rawValue = event.currentTarget.value
              setLocalInput(rawValue) // Keep what user types (like "5." or "4.5")

              const numericString = removeCommaFromPrice(rawValue)
              const parsedValue = parseFloat(numericString)

              setPriceActionWithEditMode([
                ...priceActionWithEditMode.slice(0, index),
                {
                  ...priceActionWithEditMode[index],
                  priceAction: {
                    ...priceActionWithEditMode[index].priceAction,
                    actionAmount: {
                      ...priceActionWithEditMode[index].priceAction
                        .actionAmount,
                      amount:
                        typeof parsedValue === 'number' && !isNaN(parsedValue)
                          ? parsedValue
                          : undefined
                    }
                  }
                },
                ...priceActionWithEditMode.slice(index + 1)
              ])
            }}
            inputSize="small"
            disabled={disabledByExchange}
          />
        </div>
        <div className={classes.radioButtonContainer}>
          <RadioGroup
            className={classes.radioGroup}
            value={
              priceActionWithEditMode[index].priceAction.actionAmount.type || ''
            }
            onChange={(event) =>
              setPriceActionWithEditMode([
                ...priceActionWithEditMode.slice(0, index),
                {
                  ...priceActionWithEditMode[index],
                  priceAction: {
                    ...priceActionWithEditMode[index].priceAction,
                    actionAmount: {
                      ...priceActionWithEditMode[index].priceAction
                        .actionAmount,
                      type: event.target.value as ActionAmountType
                    }
                  }
                },
                ...priceActionWithEditMode.slice(index + 1)
              ])
            }>
            <FormControlLabel
              value="Percent"
              control={<Radio color="primary" />}
              label={getTranslate('%')}
              disabled={disabledByExchange}
            />
            <FormControlLabel
              value="FixedFee"
              control={<Radio color="primary" />}
              label={getTranslate('Fixed fee')}
              disabled={disabledByExchange}
            />
          </RadioGroup>
          {priceActionWithEditMode[index].priceAction.actionType ===
            'DisplayRate' && (
            <RadioGroup
              className={classes.radioGroup}
              value={
                priceActionWithEditMode[index].priceAction.actionAmount
                  .direction || 'Decrease'
              }
              onChange={(event) =>
                setPriceActionWithEditMode([
                  ...priceActionWithEditMode.slice(0, index),
                  {
                    ...priceActionWithEditMode[index],
                    priceAction: {
                      ...priceActionWithEditMode[index].priceAction,
                      actionAmount: {
                        ...priceActionWithEditMode[index].priceAction
                          .actionAmount,
                        direction: event.target.value as ActionAmountDirection
                      }
                    }
                  },
                  ...priceActionWithEditMode.slice(index + 1)
                ])
              }>
              <FormControlLabel
                value="Increase"
                control={<Radio color="primary" />}
                label={getTranslate('Increase')}
                disabled={disabledByExchange}
              />
              <FormControlLabel
                value="Decrease"
                control={<Radio color="primary" />}
                label={getTranslate('Decrease')}
                disabled={disabledByExchange}
              />
            </RadioGroup>
          )}
          <div className={classes.currencyContainer}>
            <Typography>{getTranslate('Curr')}:</Typography>
            <Select
              value={
                priceActionWithEditMode[index].priceAction.actionAmount
                  .toCurrency || ''
              }
              onChange={(event) => {
                setPriceActionWithEditMode([
                  ...priceActionWithEditMode.slice(0, index),
                  {
                    ...priceActionWithEditMode[index],
                    priceAction: {
                      ...priceActionWithEditMode[index].priceAction,
                      actionAmount: {
                        ...priceActionWithEditMode[index].priceAction
                          .actionAmount,
                        fromCurrency: calculateFromCurrency(
                          priceActionWithEditMode[index].priceAction.actionType,
                          index > 0
                            ? priceActionWithEditMode[index - 1].priceAction
                                .actionAmount.toCurrency
                            : currencies.input,
                          event.target.value as Currency
                        ),
                        toCurrency: event.target.value as Currency
                      }
                    }
                  },
                  ...priceActionWithEditMode.slice(index + 1)
                ])
              }}>
              {getCurrenciesAvailable()
                .filter((item) =>
                  priceActionWithEditMode[index].priceAction.actionType ===
                  'Exchange'
                    ? true
                    : index > 0
                    ? item ===
                      priceActionWithEditMode[index - 1].priceAction
                        .actionAmount.toCurrency
                    : item === currencies.input
                )
                .map((currency) => (
                  <MenuItem
                    key={currency}
                    value={currency}
                    className={classes.menuItemRight}>
                    {currency}
                  </MenuItem>
                ))}
            </Select>
          </div>
        </div>
      </div>
      <div className={classes.middleSection}>
        <SelectWithLabel
          fullWidth
          label={getTranslate('Entity Type')}
          value={entityType ? entityType : null}
          onChange={(event) => {
            setEntityType(event.target.value as EntityTypeForRoleManagement)
            setPriceActionWithEditMode([
              ...priceActionWithEditMode.slice(0, index),
              {
                ...priceActionWithEditMode[index],
                priceAction: {
                  ...priceActionWithEditMode[index].priceAction,
                  beneficiary: {
                    entityID: '',
                    entityCode: '',
                    entityName: '',
                    entityType: event.target.value
                  }
                }
              },
              ...priceActionWithEditMode.slice(index + 1)
            ])
            setAutoCompleteBeneficiaryValue(null)
          }}
          disabled={disabled}
          size="small">
          {EntityTypes.map((val) => (
            <MenuItem
              key={val.value}
              value={val.value}
              className={classes.menuItemRight}>
              {getTranslate(val.label)}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <AutocompleteWithLabel
          fullWidth
          value={autoCompleteBeneficiaryValue}
          label={getTranslate('Beneficiary')}
          size="small"
          inputSize="small"
          options={loadingForEntityList ? [] : options}
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteBeneficiaryValue({
                value: v.value,
                label: v.label,
                entityCode: v.entityCode
              })
              setPriceActionWithEditMode([
                ...priceActionWithEditMode.slice(0, index),
                {
                  ...priceActionWithEditMode[index],
                  priceAction: {
                    ...priceActionWithEditMode[index].priceAction,
                    beneficiary: {
                      ...priceActionWithEditMode[index].priceAction
                        .beneficiary!,
                      entityID: v.value,
                      entityCode: v.entityCode,
                      entityName: v.label
                    }
                  }
                },
                ...priceActionWithEditMode.slice(index + 1)
              ])
            } else {
              setAutoCompleteBeneficiaryValue(null)
            }
          }}
          disabled={disabled}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          getOptionDisabled={(option: any) => option.disabled}
          loading={loadingForEntityList}
          loadingText={getTranslate('Loading') + '...'}
        />
      </div>
      <div className={classes.downSection}>
        <SelectWithLabel
          fullWidth
          value={language}
          onChange={(event) => setLanguage(event.target.value)}
          size="small"
          label={getTranslate('Select Language')}
          error={
            language === 'English' &&
            !!priceActionWithEditMode[index].priceAction.description &&
            !priceActionWithEditMode[index].priceAction
              .descriptionTranslatedList[0].value
          }
          helperText={
            language === 'English' &&
            !!priceActionWithEditMode[index].priceAction.description &&
            !priceActionWithEditMode[index].priceAction
              .descriptionTranslatedList[0].value &&
            getTranslate('Please Select Persian')
          }>
          <MenuItem
            key="English"
            value="English"
            className={classes.menuItemRight}>
            {getTranslate('English')}
          </MenuItem>
          <MenuItem
            key="Persian"
            value="Persian"
            className={classes.menuItemRight}>
            {getTranslate('Persian')}
          </MenuItem>
        </SelectWithLabel>
        {language === 'English' ? (
          <TextAreaWithLabel
            label={getTranslate('English Descriptions')}
            placeholder={getTranslate('Enter the english descriptions')}
            autoComplete="off"
            value={priceActionWithEditMode[index].priceAction.description}
            onChange={(event) =>
              setPriceActionWithEditMode([
                ...priceActionWithEditMode.slice(0, index),
                {
                  ...priceActionWithEditMode[index],
                  priceAction: {
                    ...priceActionWithEditMode[index].priceAction,
                    description: event.currentTarget.value,
                    descriptionTranslatedList: [
                      {
                        label: 'Iran, Islamic Republic of',
                        nativeLabel: 'فارسی',
                        value:
                          priceActionWithEditMode[index].priceAction
                            .descriptionTranslatedList[0].value
                      }
                    ]
                  }
                },
                ...priceActionWithEditMode.slice(index + 1)
              ])
            }
            rows={7}
            className={classes.descriptionTextArea}
          />
        ) : (
          <TextAreaWithLabel
            label={getTranslate('Persian Description')}
            placeholder={getTranslate('Enter the persian descriptions')}
            autoComplete="off"
            style={isPersian ? { fontFamily: 'IranSans' } : undefined}
            value={
              priceActionWithEditMode[index].priceAction
                .descriptionTranslatedList[0].value
            }
            onChange={(event) =>
              setPriceActionWithEditMode([
                ...priceActionWithEditMode.slice(0, index),
                {
                  ...priceActionWithEditMode[index],
                  priceAction: {
                    ...priceActionWithEditMode[index].priceAction,
                    description:
                      priceActionWithEditMode[index].priceAction.description,
                    descriptionTranslatedList: [
                      {
                        label: 'Iran, Islamic Republic of',
                        nativeLabel: 'فارسی',
                        value: event.currentTarget.value
                      }
                    ]
                  }
                },
                ...priceActionWithEditMode.slice(index + 1)
              ])
            }
            rows={7}
            className={classes.descriptionTextArea}
          />
        )}
      </div>
      <div className={classes.buttonsContainer}>
        <Button
          variant="text"
          color="primary"
          onClick={() => {
            if (
              index === priceActionWithEditMode.length - 1 &&
              initialValue.priceAction.actionType === ''
            ) {
              setPriceActionWithEditMode([
                ...priceActionWithEditMode.slice(0, index),
                ...priceActionWithEditMode.slice(index + 1)
              ])
            } else {
              setPriceActionWithEditMode([
                ...priceActionWithEditMode.slice(0, index),
                {
                  ...priceActionWithEditMode[index],
                  editing: false,
                  priceAction: initialValue.priceAction
                },
                ...priceActionWithEditMode.slice(index + 1)
              ])
            }
          }}>
          {getTranslate('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            addPriceAction()
          }}
          disabled={
            !priceActionWithEditMode[index].priceAction.actionType ||
            !priceActionWithEditMode[index].priceAction.actionAmount
              .fromCurrency ||
            !priceActionWithEditMode[index].priceAction.actionAmount
              .toCurrency ||
            !priceActionWithEditMode[index].priceAction.description ||
            !priceActionWithEditMode[index].priceAction
              .descriptionTranslatedList[0].value
          }
          color="primary">
          {getTranslate('Save')}
        </Button>
      </div>
    </div>
  )
}

export default AddPriceActionForm
